import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button
} from '@mui/material';

const sectionConfig = {
  events: {
    title: 'Patient Events',
    subtitle: 'Community events, workshops, and support sessions relevant to trial participation.',
    accent: 'primary',
    items: [
      {
        title: 'Community Health Fair - First Coast',
        meta: '2025-09-16 at 10:00 AM',
        description: 'Meet trial-support partners, navigators, and screening teams in one place.',
        tags: ['Community', 'Education', 'In Person']
      },
      {
        title: 'Health Planning Workshop',
        meta: '2025-09-05 at 2:00 PM',
        description: 'Prepare questions, records, and logistics for upcoming trial decisions.',
        tags: ['Planning', 'Workshop']
      },
      {
        title: 'Community Engagement Meeting',
        meta: '2025-09-22 at 11:00 AM',
        description: 'Hear updates from trial teams and community health partners supporting patients.',
        tags: ['Engagement', 'Support']
      }
    ]
  },
  alerts: {
    title: 'Patient Alerts',
    subtitle: 'Important notices, time-sensitive updates, and follow-up reminders.',
    accent: 'warning',
    items: [
      {
        title: 'Baseline lab deadline approaching',
        meta: 'Due in 2 days',
        description: 'Complete your required lab work before trial eligibility review closes.',
        tags: ['Urgent', 'Clinical']
      },
      {
        title: 'Transportation confirmation needed',
        meta: 'Action requested today',
        description: 'Confirm ride assistance for your next screening appointment.',
        tags: ['Logistics', 'Support']
      }
    ]
  },
  invites: {
    title: 'Invites',
    subtitle: 'Invitations from trial teams, resource partners, and education sessions.',
    accent: 'secondary',
    items: [
      {
        title: 'Invite to trial orientation session',
        meta: 'Hosted by Clinical Trial Management Team',
        description: 'Join a guided introduction to the trial timeline, consent, and next steps.',
        tags: ['Orientation', 'Team']
      },
      {
        title: 'Support partner intake invitation',
        meta: 'Community Wellness Partners',
        description: 'Connect with a resource coordinator to plan transportation and family support.',
        tags: ['Resource', 'Intake']
      }
    ]
  }
};

const PatientQuickAccessPage = ({ section = 'events' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedItem = location.state?.selectedItem;

  const config = useMemo(() => sectionConfig[section] || sectionConfig.events, [section]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Button variant="text" sx={{ mb: 2, fontWeight: 600 }} onClick={() => navigate('/dashboard')}>
        ← Back to Patient Portal
      </Button>

      <Card sx={{ mb: 3, borderRadius: 3, border: 1, borderColor: 'divider', boxShadow: 0 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>{config.title}</Typography>
          <Typography variant="body2" color="text.secondary">{config.subtitle}</Typography>
          {selectedItem?.title && (
            <Box sx={{ mt: 2 }}>
              <Chip color={config.accent} label={`Selected: ${selectedItem.title}`} />
            </Box>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        {config.items.map((item) => (
          <Grid item xs={12} md={6} key={item.title}>
            <Card sx={{ borderRadius: 2.5, border: 1, borderColor: 'divider', boxShadow: 0, height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>{item.title}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>{item.meta}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{item.description}</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {item.tags.map((tag) => (
                    <Chip key={tag} size="small" variant="outlined" color={config.accent} label={tag} />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default PatientQuickAccessPage;
